import * as MedicationApi from "api/medication.api";
import { Dispatch } from "redux";
import {
  CREATE_MEDICATION_START,
  CREATE_MEDICATION_SUCCESS,
  EDIT_ACTIVE_MEDICATION,
  LOAD_ACTIVE_MEDICATION_ERROR,
  LOAD_ACTIVE_MEDICATION_START,
  LOAD_ACTIVE_MEDICATION_SUCCESS,
  MedicationAction,
  NEW_MEDICATION_PREFILL_START,
  NEW_MEDICATION_PREFILL_SUCCESS,
  REMOVE_MEDICATION,
  REPEAT_MED_PREFILL_START,
  REPEAT_MED_PREFILL_SUCCESS,
  RESET_ACTIVE_MEDICATION,
  SELECT_ACTIVE_MEDICATION,
  STOP_MEDICATION_START,
  STOP_MEDICATION_SUCCESS,
  TOGGLE_SELECT_MEDICATION,
} from "../types/medication.types";
import {
  MedicationEntryPrescriptionStatusEnum,
  MedicationEntryType,
} from "model/medication-entry.model";
import { MedicationSearchResult } from "model/medication-search-result.model";
import { ThunkAction } from "redux-thunk";
import { GlobalState } from "store/configure-store";
import { PaginatedMedicationRequest } from "model/paginated-medication-request.model";

/** ---- ACTIONS ---- */
export const loadActiveMedication = (userId: number, patientId: number) => {
  return async (dispatch: Dispatch<MedicationAction>) => {
    try {
      dispatchLoadActiveMedicationStart()(dispatch);
      const medicationEntries = await MedicationApi.findActive(
        userId,
        patientId,
        new PaginatedMedicationRequest([])
      );
      dispatchLoadActiveMedicationSuccess(medicationEntries)(dispatch);
    } catch (e) {
      dispatchLoadActiveMedicationError()(dispatch);
    }
  };
};

export const stopMedicationEntries = (
  patientId: number,
  caregiverId: number,
  medicationEntries: Array<MedicationEntryType>,
  subContactId: number
): ThunkAction<
  Promise<Array<MedicationEntryType>>,
  GlobalState,
  any,
  MedicationAction
> => {
  return async (dispatch) => {
    dispatchStopMedicationEntriesStart(medicationEntries)(dispatch);
    const updatedEntries = medicationEntries.map((entry) =>
      Object.assign(entry, { subContactsIDs: [subContactId] })
    );
    const stoppedEntries = [];
    for (let entry of updatedEntries) {
      stoppedEntries.push(
        await MedicationApi.stopMedicationEntry(patientId, caregiverId, entry)
      );
    }
    dispatchStopMedicationEntriesSuccess(
      medicationEntries,
      stoppedEntries
    )(dispatch);
    return Promise.resolve(updatedEntries);
  };
};

export const createMedicationEntries = (
  patientId: number,
  caregiverId: number,
  medicationEntries: Array<MedicationEntryType>,
  subContactId: number
): ThunkAction<
  Promise<Array<MedicationEntryType>>,
  GlobalState,
  any,
  MedicationAction
> => {
  return async (dispatch) => {
    const updatedEntries = medicationEntries.map((entry) =>
      Object.assign(entry, { subContactsIDs: [subContactId] })
    );

    dispatchCreateMedicationEntriesStart(updatedEntries)(dispatch);
    const newMedicationEntries = await MedicationApi.createMedicationEntries(
      patientId,
      caregiverId,
      medicationEntries
    );
    dispatchCreateMedicationEntriesSuccess(newMedicationEntries)(dispatch);
    return Promise.resolve(newMedicationEntries);
  };
};

export const selectActiveMedication = (
  medicationEntry: MedicationEntryType
) => {
  return async (dispatch: Dispatch<MedicationAction>) => {
    dispatchSelectActiveMedication(medicationEntry)(dispatch);
  };
};

export const newMedication = (
  patientId: number,
  userId: number,
  medicationSearchResult: MedicationSearchResult
) => {
  return async (dispatch: Dispatch<MedicationAction>) => {
    try {
      dispatchNewMedicationPrefillStart()(dispatch);
      const medicationEntry = await MedicationApi.prefillSearchResult(
        userId,
        patientId,
        medicationSearchResult
      );
      dispatchNewMedicationSuccess(medicationEntry)(dispatch);
    } catch (e) {
      dispatchNewMedicationError()(dispatch);
    }
  };
};

export const resetActiveMedication = () => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatchResetActiveMedication()(dispatch);
  };
};

export const editActiveMedication = (medicationEntry: MedicationEntryType) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatchEditActiveMedication(medicationEntry)(dispatch);
  };
};

export const toggleSelectedMedication = (
  patientId: number,
  userId: number,
  medicationEntry: MedicationEntryType
) => {
  return async (dispatch: Dispatch<MedicationAction>) => {
    if (
      medicationEntry.id >= 0 &&
      medicationEntry.prescriptionStatus ===
        MedicationEntryPrescriptionStatusEnum.DEFAULT
    ) {
      dispatchPrefillMedicationEntryStart(medicationEntry)(dispatch);
      const newEntry = await MedicationApi.prefillForRepeat(
        userId,
        patientId,
        medicationEntry
      );
      dispatchPrefillMedicationEntrySuccess(
        medicationEntry,
        newEntry
      )(dispatch);
    } else if (
      medicationEntry.prescriptionStatus !==
      MedicationEntryPrescriptionStatusEnum.PREFILL
    ) {
      dispatchToggleSelectMedicationEntry(medicationEntry)(dispatch);
    }
  };
};

export const removeMedication = (medicationEntry: MedicationEntryType) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatchRemoveMedication(medicationEntry)(dispatch);
  };
};

/** ---- DISPATCHERS ---- */
const dispatchRemoveMedication = (entry: MedicationEntryType) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({ type: REMOVE_MEDICATION, payload: entry });
  };
};

const dispatchCreateMedicationEntriesSuccess = (
  entries: Array<MedicationEntryType>
) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({ type: CREATE_MEDICATION_SUCCESS, payload: entries });
  };
};

const dispatchStopMedicationEntriesStart = (
  entries: Array<MedicationEntryType>
) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({ type: STOP_MEDICATION_START, payload: entries });
  };
};

const dispatchStopMedicationEntriesSuccess = (
  oldEntries: Array<MedicationEntryType>,
  newEntries: Array<MedicationEntryType>
) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: STOP_MEDICATION_SUCCESS,
      payload: { old: oldEntries, new: newEntries },
    });
  };
};

const dispatchCreateMedicationEntriesStart = (
  entries: Array<MedicationEntryType>
) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({ type: CREATE_MEDICATION_START, payload: entries });
  };
};

const dispatchPrefillMedicationEntryStart = (
  medicationEntry: MedicationEntryType
) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({ type: REPEAT_MED_PREFILL_START, payload: medicationEntry });
  };
};

const dispatchPrefillMedicationEntrySuccess = (
  oldMedicationEntry: MedicationEntryType,
  medicationEntry: MedicationEntryType
) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: REPEAT_MED_PREFILL_SUCCESS,
      payload: { old: oldMedicationEntry, new: medicationEntry },
    });
  };
};

const dispatchNewMedicationPrefillStart = () => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({ type: NEW_MEDICATION_PREFILL_START });
  };
};

const dispatchNewMedicationSuccess = (medicationEntry: MedicationEntryType) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: NEW_MEDICATION_PREFILL_SUCCESS,
      payload: medicationEntry,
    });
  };
};

const dispatchNewMedicationError = () => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: "NEW_MEDICATION_PREFILL_ERROR",
    });
  };
};

const dispatchToggleSelectMedicationEntry = (
  medicationEntry: MedicationEntryType
) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: TOGGLE_SELECT_MEDICATION,
      payload: medicationEntry,
    });
  };
};

const dispatchResetActiveMedication = () => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: RESET_ACTIVE_MEDICATION,
    });
  };
};

const dispatchLoadActiveMedicationStart = () => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: LOAD_ACTIVE_MEDICATION_START,
    });
  };
};

const dispatchLoadActiveMedicationSuccess = (
  response: MedicationEntryType[]
) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: LOAD_ACTIVE_MEDICATION_SUCCESS,
      payload: response,
    });
  };
};

const dispatchLoadActiveMedicationError = () => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: LOAD_ACTIVE_MEDICATION_ERROR,
    });
  };
};

const dispatchSelectActiveMedication = (response: MedicationEntryType) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: SELECT_ACTIVE_MEDICATION,
      payload: response,
    });
  };
};

const dispatchEditActiveMedication = (response: MedicationEntryType) => {
  return (dispatch: Dispatch<MedicationAction>) => {
    dispatch({
      type: EDIT_ACTIVE_MEDICATION,
      payload: response,
    });
  };
};
